import _ from 'lodash';
import Promise from 'bluebird';
import pathToRegexp from 'path-to-regexp';
import CustomerActions from '../../../actions/CustomerActions';
import ShoppingCartActions from '../../../actions/ShoppingCartActions';
import CustomerStore from '../../../stores/CustomerStore';
import ShoppingCartStore from '../../../stores/ShoppingCartStore';
import config from '../../../config/client';

const isBrowser = (typeof window !== 'undefined');

const FilterAbbreviationsMapping = {
  region: 'r',
  varietal: 'v',
  wine_pairing: 'p',
  min_price_in_cents: 'minp',
  max_price_in_cents: 'maxp',
  color: 'c',
  carbonation: 'carb',
  alcohol_type: 'a',
  volume: 'vol',
  tag: 't',
  text: 'q',
  featured_on_banquet: 'f'
};
const AbbreviatedFiltersMapping = _.invert(FilterAbbreviationsMapping);

const SorterAbbreviationsMapping = {
  rating: 'r',
  price: 'p',
  created_at: 'c'
};
const AbbreviatedSortersMapping = _.invert(SorterAbbreviationsMapping);

const QueryParamAbbreviationsMapping = {
  filters: 'f',
  sorter: 's'
};
const AbbreviatedQueryParamsMapping = _.invert(QueryParamAbbreviationsMapping);

const CategoryImplicitFiltersMapping = {
  'regions': [],
  'red-wine': [{type: 'color', value: 'red'}, {type: 'alcohol_type', value: 'wine'}],
  'white-wine': [{type: 'color', value: 'white'}, {type: 'alcohol_type', value: 'wine'}],
  'beer': [{type: 'alcohol_type', value: 'beer'}],
  'spirits': [{type: 'alcohol_type', value: 'liquor'}],
  'sparkling': [{type: 'carbonation', value: 'sparkling'}, {type: 'alcohol_type', value: 'wine'}],
  'new-arrivals': [],
  'staff-picks': [{type: 'featured_on_banquet', value: true}]
};
const CategoryImplicitSorterMapping = {
  'regions': null,
  'red-wine': null,
  'white-wine': null,
  'beer': null,
  'spirits': null,
  'sparkling': null,
  'new-arrivals': {key: 'created_at', direction: 'desc'},
  'staff-picks': null
};
const CategoryWildcardFiltersTypeMapping = {
  'regions': 'region',
  'red-wine': 'varietal',
  'white-wine': 'varietal',
  'beer': 'alcohol_type',
  'spirits': 'alcohol_type',
  'sparkling': null,
  'new-arrivals': null,
  'staff-picks': null
};

// Unfortunately these had to be decoupled and separated from ./Routes :-/
// Delectory API needs to be able to load this lib (loadPathsOnly) without pulling
// in all of the route dependencies (actions, handlers, .etc) that don't run within
// that environment. (no webpack)
const RouteNamePathsMapping = {
  // Static Routes
  home: '/',
  pricing: '/sell-on-banquet',
  storesList: '/customers',
  download: '/download',
  wineSku: '/products/a/:slug/:id',
  universalSearch: '/search',
  // Dynamic StoreOptional Routes
  signIn: '/sign-in',
  setPassword: '/set-password',
  activateAccount: '/activate-account',
  settings: '/settings',
  signOut: '/sign-out',
  // Dynamic StoreRequired Routes
  storeLandingPage: '/',
  storeAboutPage: '/about',
  refundPolicy: '/return-and-refund-policy',
  termsOfService: '/terms-of-service',
  bag: '/bag',
  saved: '/saved',
  checkout: '/checkout',
  orderConfirmation: '/confirmation',
  orderHistory: '/order-history',
  order: '/order-history/:id',
  collections: '/collections',
  collection: '/collections/:slug',
  inventoryRecord: '/products/:slug/:id',
  supplierInventoryRecord: '/inventory/:sku',
  collectionInventoryRecord: '/collections/:collectionSlug/products/:slug/:id',
  categoryList: '/:type(regions|red-wine|white-wine|beer|spirits)',
  sparkling: '/:type(sparkling)',
  category: '/:type(regions|red-wine|white-wine|beer|spirits|sparkling|new-arrivals|staff-picks)/:filters*',
  search: '/search',
  // Embeds Routes
  embeddedDownloadBanner: '/embeds/download-banner/:storeId'
};

class BanquetRouteManager {
  constructor(options={}) {
    _.defaults(options, {loadPathsOnly: false});
    this.storeConfig = options.storeConfig;
    this._routes = null;
    this._setRoutes(options.loadPathsOnly);
  }

  encodeQueryParams(queryParams) {
    if(!_.size(queryParams)) {return '';}
    let queryString = _.reduce(queryParams, (encodedQueryParams, val, key) => {
      let valueString;
      switch(key) {
        case 'filters':
          if(!(val instanceof Array) || !val.length) {return encodedQueryParams;}
          valueString = _.chain(val)
            .uniq((f) => {return `${f.type}:${f.value}`;})
            .filter((f) => {return FilterAbbreviationsMapping[f.type];})
            .map((f) => {return `${FilterAbbreviationsMapping[f.type]}:${encodeURIComponent(f.value.toString()).replace(/%20/g, '+')}`;})
            .value()
            .join(',');
          break;
        case 'sorter':
          if(!val || !val.key || !val.direction || !SorterAbbreviationsMapping[val.key]) {return encodedQueryParams;}
          valueString = `${SorterAbbreviationsMapping[val.key]}:${val.direction === 'desc' ? 'd' : 'a'}`;
          break;
        default:
          break;
      }
      if(!valueString) {return encodedQueryParams;}
      return encodedQueryParams.concat(`${QueryParamAbbreviationsMapping[key]}=${valueString}`);
    }, []).join(';');
    if(!queryString) {return '';}
    return `${queryString};`;
  }

  decodeQueryParams(queryString) {
    let filters = [], sorter = null;
    return _.reduce(queryString.split(';'), (results, grouping) => {
      let [abbreviatedQueryParamKey, valueString] = grouping.split('=');
      if(!abbreviatedQueryParamKey || !valueString) {return results;}
      switch(AbbreviatedQueryParamsMapping[abbreviatedQueryParamKey]) {
        case 'filters':
          results.filters = results.filters || [];
          results.filters = results.filters.concat(
            _.map(valueString.split(','), (filterString) => {
              let [abbreviatedType, encodedValueString] = filterString.split(':');
              let type = AbbreviatedFiltersMapping[abbreviatedType];
              let value = decodeURIComponent(encodedValueString.replace(/\+/g, '%20'));
              if(type.match(/_price_in_cents$/)) {value = parseInt(value);}
              return {type, value};
            })
          );
          break;
        case 'sorter':
          let [abbreviatedKey, abbreviatedDirection] = valueString.split(':');
          let key = AbbreviatedSortersMapping[abbreviatedKey];
          let direction = abbreviatedDirection === 'd' ? 'desc' : 'asc';
          results.sorter = {key, direction};
          break;
        default:
          break;
      }
      return results;
    }, {});
  }

  extractImplicitQueryParamsFromRoute(routeName, params) {
    let results = {
      filters: [],
      sorter: null
    };
    switch(routeName) {
      case 'category':
      case 'sparkling':
        results.filters = results.filters.concat(CategoryImplicitFiltersMapping[params.type]);
        let wildcardFilterType = CategoryWildcardFiltersTypeMapping[params.type];
        if(wildcardFilterType && params.filters) {
          results.filters = results.filters.concat(
            _.map(params.filters.split('/'), (encodedFilterString) => {
              return {type: wildcardFilterType, value: decodeURIComponent(encodedFilterString.replace(/-/g, ' '))};
            })
          );
        }
        results.sorter = CategoryImplicitSorterMapping[params.type];
        break;
      default:
        break;
    }
    return results;
  }

  partitionParamsForRoute(routeName, params, query) {
    // Ignore implicit filters/sorters, separate into params and query for URL building
    let results = {
      params: _.extend({}, params),
      query: _.extend({}, query)
    };
    switch(routeName) {
      case 'category':
      case 'sparkling':
        results.params.filters = [];
        results.params.sorter = null;
        results.query.filters = [];
        results.query.sorter = null;
        let implicitFilters = CategoryImplicitFiltersMapping[params.type];
        let wildcardFilterType = CategoryWildcardFiltersTypeMapping[params.type];
        _.each(_.uniq(params.filters, (f) => {return `${f.type}:${f.value}`;}), (f) => {
          if(_.find(implicitFilters, {type: f.type, value: f.value})) {return;}
          if(f.type === wildcardFilterType) {
            // NOTE: path-to-regex encodes these guys
            results.params.filters.push(f.value.toLowerCase().replace(/ /g, '-'));
          } else {
            results.query.filters.push(f);
          }
        });
        let implicitSorter = CategoryImplicitSorterMapping[params.type];
        if(params.sorter && !_.isEqual(params.sorter, implicitSorter)) {
          results.query.sorter = params.sorter;
        }
        break;
      case 'search':
      case 'universalSearch':
        results.query.filters = results.query.filters || [];
        if(params.filters && params.filters.length) {
          results.query.filters = results.query.filters.concat(params.filters);
        }
        if(params.sorter) {
          results.query.sorter = params.sorter;
        }
        break;
      default:
        break;
    }
    return results;
  }

  buildURLForRoute(routeName, params, query, options={}) {
    _.defaults(options, {relative: true});

    if(!this._routes.hasOwnProperty(routeName)) {
      throw new Error(`"${routeName}" is not a valid route.`);
    }
    let routeSpec = this._routes[routeName];
    let partitionedParams = this.partitionParamsForRoute(routeName, params, query);
    // 'category' can fallback to 'search' if no matching filters found
    if(routeName === 'category' && !partitionedParams.params.filters.length) {
      routeName = 'search';
    }
    let toPath = pathToRegexp.compile(routeSpec.path);
    let path = toPath(partitionedParams.params);

    if(_.size(partitionedParams.query)) {
      let queryString = this.encodeQueryParams(partitionedParams.query);
      if(queryString) {
        path += `?${queryString}`;
      }
    }

    if(options.relative) {
      return path;
    }

    return `${this.getDomain()}${path}`;
  }

  getDomain() {
    return (this.storeConfig && this.storeConfig.domain) ? this.storeConfig.domain : config.BanquetUrl;
  }

  getRoutes() {
    return this._routes;
  }

  getRouteSpec(routeName) {
    if(!this._routeSpecs) {
      this._routeSpecs = require('./Routes.js').default;
      this._routeTypes = require('./Routes.js').RouteTypes;
    }
    return _.find(this._routeSpecs, (routeDef) => {
      return routeDef.name === routeName && !(this.storeConfig && routeDef.type === this._routeTypes.Marketing);
    });
  }

  makeAbsoluteURLFromPath(path) {
    return ((this.storeConfig && this.storeConfig.domain) ? this.storeConfig.domain : config.BanquetUrl) + path;
  }

  _setRoutes(loadPathsOnly) {
    this._routes = {};

    for(let routeName in RouteNamePathsMapping) {
      let path = RouteNamePathsMapping[routeName];
      if(this.storeConfig) {
        // Override with storeConfig prefix
        path = this.storeConfig.domain ? path : `/${this.storeConfig.slug}${path}`;
        if(path.length > 1) {
          path = path.replace(/\/$/, '');
        }
      }
      // Skip loading ./Routes.js which is dependent on many React components/sass files
      if(loadPathsOnly) {
        this._routes[routeName] = {path};
        continue;
      }
      let routeSpec = this.getRouteSpec(routeName);
      if(!routeSpec || (!this.storeConfig && routeSpec.type === this._routeTypes.StoreRequired)) {
        continue;
      }
      let action;
      if(this.storeConfig) {
        // Override `action` with a wrapper function to check for base dependencies
        action = (context, route) => {
          let baseDeps;
          if(baseDeps = hasBaseDependenciesForStoreRoute(context, route)) {
            return routeSpec.action ? routeSpec.action(context, route, baseDeps) : null;
          }
          return fetchBaseDependenciesForStoreRoute(context, route).then((baseDeps) => {
            return routeSpec.action ? routeSpec.action(context, route, baseDeps) : null;
          });
        };
      } else {
        // StoreOptional and not present
        action = (context, route) => {
          let customer = context.getStore(CustomerStore).getCurrentCustomer();
          return routeSpec.action ? routeSpec.action(context, route, {customer}) : null;
        };
      }

      this._routes[routeName] = {
        ...routeSpec,
        method: 'GET',
        path: path,
        action: action
      };
    }
  }
}

export default BanquetRouteManager;

const TenMinutes = 1000 * 60 * 60;
let lastBaseDependencyFetch = 9999999999999;

function hasBaseDependenciesForStoreRoute(context, route) {
  if(!isBrowser) { return false; }
  if(!lastBaseDependencyFetch || (lastBaseDependencyFetch <= Date.now() - TenMinutes)) {return false;}

  let store = context.getCurrentStore();
  let customer = context.getStore(CustomerStore).getCurrentCustomer();
  if(!customer) {return false;}

  let shoppingCart = context.getStore(ShoppingCartStore).getShoppingCartByStoreId(store.id);
  if(!shoppingCart) {return false;}

  return {customer, store, shoppingCart};
}

function fetchBaseDependenciesForStoreRoute(context, route) {
  return Promise.all([
    context.executeAction(CustomerActions.findOrCreate, {}),
    context.executeAction(ShoppingCartActions.fetch, {store_id: context.getCurrentStore().id})
  ]).then((values) => {
    lastBaseDependencyFetch = Date.now();
    let [customer, shoppingCart] = values;
    let store = context.getCurrentStore();
    return {customer, store, shoppingCart};
  });
}



// WEBPACK FOOTER //
// banquet/lib/BanquetRouteManager/lib/BanquetRouteManager.js